import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import api from "@/lib/axios";
import { toast } from "sonner";
import { 
  Users, 
  Calendar, 
  Mail, 
  BarChart3, 
  ChevronRight,
  Clock,
  CheckCircle2,
  XCircle,
  Trash2,
  ArrowLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Link } from "react-router-dom";



const AdminPage = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [stats, setStats] = useState({
    total_consultations: 0,
    total_contacts: 0,
    pending_consultations: 0,
    new_contacts: 0,
  });
  const [consultations, setConsultations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, consultRes, contactRes] = await Promise.all([
        api.get("/stats"),
        api.get("/consultations"),
        api.get("/contacts"),
      ]);
      setStats(statsRes.data);
      setConsultations(consultRes.data);
      setContacts(contactRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const updateConsultationStatus = async (id, status) => {
    try {
      await api.patch(`/contacts/${id}`, { status });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const updateContactStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contacts/${id}`, { status });
      toast.success("Status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteConsultation = async (id) => {
    try {
      await axios.delete(`${API}/consultations/${id}`);
      toast.success("Consultation deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const deleteContact = async (id) => {
    try {
      await api.delete(`/contacts/${id}`);
      toast.success("Contact deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      contacted: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      completed: "bg-green-500/20 text-green-400 border-green-500/30",
      cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
      new: "bg-purple-500/20 text-purple-400 border-purple-500/30",
      responded: "bg-cyan-500/20 text-cyan-400 border-cyan-500/30",
    };
    return variants[status] || variants.new;
  };

  const navItems = [
    { id: "dashboard", label: "Dashboard", icon: BarChart3 },
    { id: "consultations", label: "Consultations", icon: Calendar },
    { id: "contacts", label: "Contacts", icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-void flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white/[0.02] border-r border-white/10 flex flex-col">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Site</span>
          </Link>
          <h1 className="font-unbounded font-bold text-xl text-white mt-4">
            GuruTech<span className="text-electric-violet">.</span>Admin
          </h1>
        </div>

        <nav className="flex-1 p-4">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`admin-nav-item w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-all ${
                activeTab === item.id
                  ? "bg-white/5 text-white"
                  : "text-muted-foreground hover:text-white hover:bg-white/[0.02]"
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
              {activeTab === item.id && (
                <ChevronRight className="w-4 h-4 ml-auto" />
              )}
            </button>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        {activeTab === "dashboard" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="admin-dashboard"
          >
            <h2 className="font-unbounded font-bold text-2xl text-white mb-8">
              Dashboard Overview
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="Total Consultations"
                value={stats.total_consultations}
                icon={Calendar}
                color="violet"
              />
              <StatCard
                title="Pending Consultations"
                value={stats.pending_consultations}
                icon={Clock}
                color="yellow"
              />
              <StatCard
                title="Total Contacts"
                value={stats.total_contacts}
                icon={Mail}
                color="cyan"
              />
              <StatCard
                title="New Contacts"
                value={stats.new_contacts}
                icon={Users}
                color="green"
              />
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="glass-card p-6">
                <h3 className="font-unbounded font-medium text-lg text-white mb-4">
                  Recent Consultations
                </h3>
                <div className="space-y-4">
                  {consultations.slice(0, 5).map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg"
                    >
                      <div>
                        <p className="text-white font-medium">{c.name}</p>
                        <p className="text-sm text-muted-foreground">{c.email}</p>
                      </div>
                      <Badge className={getStatusBadge(c.status)}>{c.status}</Badge>
                    </div>
                  ))}
                  {consultations.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No consultations yet</p>
                  )}
                </div>
              </div>

              <div className="glass-card p-6">
                <h3 className="font-unbounded font-medium text-lg text-white mb-4">
                  Recent Contacts
                </h3>
                <div className="space-y-4">
                  {contacts.slice(0, 5).map((c) => (
                    <div
                      key={c.id}
                      className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg"
                    >
                      <div>
                        <p className="text-white font-medium">{c.name}</p>
                        <p className="text-sm text-muted-foreground">{c.email}</p>
                      </div>
                      <Badge className={getStatusBadge(c.status)}>{c.status}</Badge>
                    </div>
                  ))}
                  {contacts.length === 0 && (
                    <p className="text-muted-foreground text-center py-4">No contacts yet</p>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === "consultations" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="admin-consultations"
          >
            <h2 className="font-unbounded font-bold text-2xl text-white mb-8">
              Consultation Requests
            </h2>

            <div className="glass-card">
              <ScrollArea className="h-[calc(100vh-200px)]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-muted-foreground">Name</TableHead>
                      <TableHead className="text-muted-foreground">Email</TableHead>
                      <TableHead className="text-muted-foreground">Company</TableHead>
                      <TableHead className="text-muted-foreground">Preferred Date</TableHead>
                      <TableHead className="text-muted-foreground">Status</TableHead>
                      <TableHead className="text-muted-foreground">Created</TableHead>
                      <TableHead className="text-muted-foreground">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {consultations.map((c) => (
                      <TableRow key={c.id} className="border-white/10 hover:bg-white/[0.02]">
                        <TableCell className="text-white font-medium">{c.name}</TableCell>
                        <TableCell className="text-muted-foreground">{c.email}</TableCell>
                        <TableCell className="text-muted-foreground">{c.company || "-"}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {c.preferred_date || "-"} {c.preferred_time || ""}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={c.status}
                            onValueChange={(value) => updateConsultationStatus(c.id, value)}
                          >
                            <SelectTrigger className="w-32 bg-transparent border-white/10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="contacted">Contacted</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                              <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(c.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteConsultation(c.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-consultation-${c.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {consultations.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    No consultation requests yet
                  </div>
                )}
              </ScrollArea>
            </div>
          </motion.div>
        )}

        {activeTab === "contacts" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="admin-contacts"
          >
            <h2 className="font-unbounded font-bold text-2xl text-white mb-8">
              Contact Submissions
            </h2>

            <div className="glass-card">
              <ScrollArea className="h-[calc(100vh-200px)]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-muted-foreground">Name</TableHead>
                      <TableHead className="text-muted-foreground">Email</TableHead>
                      <TableHead className="text-muted-foreground">Company</TableHead>
                      <TableHead className="text-muted-foreground">Project Description</TableHead>
                      <TableHead className="text-muted-foreground">Status</TableHead>
                      <TableHead className="text-muted-foreground">Created</TableHead>
                      <TableHead className="text-muted-foreground">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((c) => (
                      <TableRow key={c.id} className="border-white/10 hover:bg-white/[0.02]">
                        <TableCell className="text-white font-medium">{c.name}</TableCell>
                        <TableCell className="text-muted-foreground">{c.email}</TableCell>
                        <TableCell className="text-muted-foreground">{c.company || "-"}</TableCell>
                        <TableCell className="text-muted-foreground max-w-xs truncate">
                          {c.project_description}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={c.status}
                            onValueChange={(value) => updateContactStatus(c.id, value)}
                          >
                            <SelectTrigger className="w-32 bg-transparent border-white/10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="responded">Responded</SelectItem>
                              <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(c.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteContact(c.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`delete-contact-${c.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {contacts.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    No contact submissions yet
                  </div>
                )}
              </ScrollArea>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colorClasses = {
    violet: "from-electric-violet/20 to-transparent border-electric-violet/30",
    cyan: "from-neon-cyan/20 to-transparent border-neon-cyan/30",
    yellow: "from-yellow-500/20 to-transparent border-yellow-500/30",
    green: "from-green-500/20 to-transparent border-green-500/30",
  };

  const iconColors = {
    violet: "text-electric-violet",
    cyan: "text-neon-cyan",
    yellow: "text-yellow-400",
    green: "text-green-400",
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`stat-card glass-card p-6 bg-gradient-to-br ${colorClasses[color]}`}
    >
      <div className="flex items-center justify-between mb-4">
        <Icon className={`w-8 h-8 ${iconColors[color]}`} />
      </div>
      <p className="text-3xl font-unbounded font-bold text-white mb-1">{value}</p>
      <p className="text-sm text-muted-foreground">{title}</p>
    </motion.div>
  );
};

export default AdminPage;
